import React, { useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';
import { Alert, Button, Typography } from '@robinpowered/ui-kit';
import { WarningSolid } from '@robinpowered/icons';
import {
  openReconnectAccounts,
  pushSharedUrl,
  RECONNECT_ACCOUNTS_FLAG,
  useFeatureFlags,
  useReconnectSucceeded,
} from '@robinpowered/dashboard-apps-common';
import { useCalendarIntegrationHealth } from 'hooks/useCalendarIntegrationHealth';

const { Text } = Typography;

/**
 * Shell-hosted global banner telling a user their Google/Microsoft calendar
 * connection needs attention, no matter which micro-frontend they're
 * currently looking at, and opening the shell's reconnect modal.
 *
 * Gated at the root, mirroring `ReconnectAccountsHost`: a thin outer
 * component returns null while the flag is off or still loading, so the
 * health probe below never runs for a user the feature isn't on for.
 */
export const GlobalReconnectBanner = (): JSX.Element | null => {
  const { checkFlag, loading } = useFeatureFlags();

  if (!checkFlag(RECONNECT_ACCOUNTS_FLAG) || loading) {
    return null;
  }

  return <GlobalReconnectBannerEnabled />;
};

const GlobalReconnectBannerEnabled = (): JSX.Element | null => {
  const { t } = useTranslation('reconnectAccounts');
  const [searchParams] = useSearchParams();

  // Always probing while the feature is on — unlike the modal's own probe,
  // which only runs while it's open, the banner has to know a connection is
  // broken before anyone has asked to fix it, on any page.
  const { health, refetch } = useCalendarIntegrationHealth({ enabled: true });

  // A reconnect completed in the modal should clear the banner immediately,
  // without waiting on any other trigger.
  useReconnectSucceeded(() => {
    void refetch();
  });

  // Covers a reconnect finished outside this tab (another tab, the
  // provider's own account settings) — returning to this tab is the one
  // moment such a fix would otherwise go unnoticed.
  useEffect(() => {
    const handleFocus = (): void => {
      void refetch();
    };
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [refetch]);

  // Opens the shell's existing reconnect modal by setting the `reconnect`
  // param on a copy of the current URL and pushing it. Goes through
  // `pushSharedUrl` (not `setSearchParams`) so every Router on the page —
  // not just shell's — observes the change, matching how an MFE opens the
  // same modal via `useShellReconnectNavigation`.
  const openModal = useCallback(() => {
    const params = new URLSearchParams(searchParams);
    openReconnectAccounts(params);
    const query = params.toString();
    pushSharedUrl(`${window.location.pathname}${query ? `?${query}` : ''}`);
  }, [searchParams]);

  // Silent for loading, unavailable, and the empty case: a global banner
  // that nags when we couldn't determine status, or when everything's
  // fine, would train users to ignore it.
  if (health.state !== 'ready' || health.needsAttention.length === 0) {
    return null;
  }

  return (
    <BannerAlert
      message={
        <ContentWrapper>
          <WarningSolid size={16} />
          <BannerText>
            {t(
              health.needsAttention.length === 1
                ? 'banner.message_single'
                : 'banner.message_multiple'
            )}
          </BannerText>
          <ReconnectButton type="primary" size="small" onClick={openModal}>
            {t('banner.cta')}
          </ReconnectButton>
        </ContentWrapper>
      }
      banner={true}
      showIcon={false}
      closable={false}
      data-testid="global-reconnect-banner"
    />
  );
};

const BannerAlert = styled(Alert)`
  align-items: center;
  align-self: stretch;
  background: var(--Colors-Neutral-Bg-colorBgSpotlight, rgba(0, 0, 0, 0.85));
  display: flex;
  gap: var(--Components-Alert-Global-marginXS, 8px);
  justify-content: center;
  padding: var(--Space-Padding-paddingContentVerticalSM, 8px)
    var(--Space-Padding-paddingSM, 12px);

  .ant-alert-title {
    width: 100%;
    display: flex;
    justify-content: center;
  }
`;

const ContentWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: var(--Colors-Neutral-Text-colorTextLightSolid, #fff);

  path {
    fill: var(--Colors-Neutral-Text-colorTextLightSolid, #fff);
  }
`;

const BannerText = styled(Text)`
  color: var(--Colors-Neutral-Text-colorTextLightSolid, #fff);
`;

const ReconnectButton = styled(Button)`
  flex-shrink: 0;
`;
